<script setup lang="ts">
interface Props {
  compact?: boolean;
  count: number | undefined;
  // Sits in the flow of a caption bar instead of over the image.
  inline?: boolean;
}

withDefaults(defineProps<Props>(), {
  compact: false,
  inline: false,
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  decrement: [event: MouseEvent];
  increment: [event: MouseEvent];
}>();
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__counter"
    :class="{
      'ClassicMixedPortraitGallery__counter--compact': compact,
      'ClassicMixedPortraitGallery__counter--inline': inline,
    }"
    data-testid="portrait-counter"
  >
    <button
      class="ClassicMixedPortraitGallery__counter-button kiosk-button"
      type="button"
      data-testid="portrait-counter-dec"
      @click.stop="emit('decrement', $event)"
    >
      &minus;
    </button>
    <span
      class="ClassicMixedPortraitGallery__counter-number"
      data-testid="portrait-counter-value"
    >
      {{ count }}
    </span>
    <button
      class="ClassicMixedPortraitGallery__counter-button kiosk-button"
      type="button"
      data-testid="portrait-counter-inc"
      @click.stop="emit('increment', $event)"
    >
      +
    </button>
  </div>
</template>

<style scoped lang="scss">
/* Every button is at least a finger wide (--cmg-touch) in both sizes. */
.ClassicMixedPortraitGallery__counter {
  position: absolute;
  bottom: 1.2rem;
  left: 50%;
  z-index: 4;
  display: flex;
  align-items: center;
  padding: 0.35rem 0.45rem;
  color: var(--Kiosk--button_font_color);
  background: var(--Kiosk--button_background_color);
  border-radius: 3rem;
  box-shadow: 0 0.3rem 1.4rem rgba(0, 0, 0, 0.25);
  transform: translateX(-50%);
}

.ClassicMixedPortraitGallery__counter-button {
  --kiosk-button-width: var(--cmg-touch);
  --kiosk-button-min-width: var(--cmg-touch);
  --kiosk-button-height: var(--cmg-touch);
  --kiosk-button-min-height: var(--cmg-touch);
  --kiosk-button-padding: 0;
  --kiosk-button-font-size: 2rem;
  --kiosk-button-color: var(--Kiosk--button_background_color);
  --kiosk-button-background: var(--Kiosk--button_font_color);
  --kiosk-button-border-radius: 100%;

  touch-action: manipulation;
}

.ClassicMixedPortraitGallery__counter-number {
  min-width: 3rem;
  padding: 0 0.7rem;
  font-size: 1.6rem;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.ClassicMixedPortraitGallery__counter--compact {
  bottom: 0.45rem;
  padding: 0.2rem 0.3rem;
}

.ClassicMixedPortraitGallery__counter--compact
  .ClassicMixedPortraitGallery__counter-button {
  --kiosk-button-font-size: 1.5rem;
}

.ClassicMixedPortraitGallery__counter--compact
  .ClassicMixedPortraitGallery__counter-number {
  min-width: 2rem;
  padding: 0 0.4rem;
  font-size: 1.2rem;
}

.ClassicMixedPortraitGallery__counter--inline {
  position: static;
  box-shadow: none;
  transform: none;
}
</style>
